import React, { createContext, useState, useContext, useEffect } from 'react';
import API from '../config/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Check if user is logged in on mount
  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setLoading(false);
        return;
      }

      const response = await API.get('/auth/verify');
      if (response.data.success) {
        setUser(response.data.user);
        setIsAuthenticated(true);
      } else {
        localStorage.removeItem('token');
        localStorage.removeItem('remember_me');
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      localStorage.removeItem('token');
      localStorage.removeItem('remember_me');
    } finally {
      setLoading(false);
    }
  };

  const login = async (username, password, rememberMe) => {
    try {
      const response = await API.post('/auth/login', {
        username,
        password,
        remember_me: rememberMe
      });

      if (response.data.success) {
        localStorage.setItem('token', response.data.token);
        if (rememberMe) {
          localStorage.setItem('remember_me', 'true');
          // Store username in cookie (for remember me functionality)
          document.cookie = `username=${username}; max-age=${30 * 24 * 60 * 60}; path=/`;
        } else {
          localStorage.removeItem('remember_me');
          document.cookie = 'username=; max-age=0; path=/';
        }

        setUser(response.data.user);
        setIsAuthenticated(true);
        return { success: true, user: response.data.user };
      } else {
        return { success: false, message: response.data.message };
      }
    } catch (error) {
      console.error('Login error:', error);
      console.error('Error response:', error.response);
      const message = error.response?.data?.message || error.message || 'Login failed. Please try again.';
      return { success: false, message };
    }
  };

  const logout = async () => {
    try {
      await API.post('/auth/logout');
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('token');
      localStorage.removeItem('remember_me');
      localStorage.removeItem('qurexr_chat_history'); // Clear Help Desk chat history
      document.cookie = 'username=; max-age=0; path=/';
      setUser(null);
      setIsAuthenticated(false);
    }
  };

  // Helper function to get redirect path based on user type (matching PHP logic)
  const getRedirectPath = (userType) => {
    const type = (userType || '').toLowerCase();
    switch (type) {
      case 'qurator':
        return '/qurator-dashboard';
      case 'qure':
        return '/qure-dashboard';
      case 'admin':
      case 'super admin':
        return '/admin-dashboard';
      default:
        return '/qurator-dashboard';
    }
  };

  const value = {
    user,
    loading,
    isAuthenticated,
    login,
    logout,
    checkAuth,
    getRedirectPath
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
